import React, { useState } from 'react';
import { decodeJWTToken } from '../utils/auth.ts';
import { Code, User, Eye, EyeOff } from 'lucide-react';

const TokenDecoder: React.FC = () => {
  const [token, setToken] = useState('');
  const [showToken, setShowToken] = useState(false);
  const [decodedProfile, setDecodedProfile] = useState<any>(null);

  // Your example token for testing
  const exampleToken = "eyJhbGciOiJIUzI1NiIsInR5cCI6IkpXVCJ9.eyJpZCI6IjY4OTc4NTc4NTM3ZDk0NzI2YjFhZjBkOCIsImVtYWlsIjoibmF1YmFkbml0aGluQGdtYWlsLmNvbSIsIm5hbWUiOiJOaXRoaW4gTmF1YmFkIiwiaWF0IjoxNzU0NzYyOTUxLCJleHAiOjE3NTQ4NDkzNTF9.2pSBZRdI2aTIptmPQNVQchKYQU0eaQy98isKZwgEg2k";

  const handleDecode = () => {
    const tokenToDecode = token || exampleToken;
    const profile = decodeJWTToken(tokenToDecode);
    setDecodedProfile(profile);
  };

  const loadExampleToken = () => {
    setToken(exampleToken);
    const profile = decodeJWTToken(exampleToken);
    setDecodedProfile(profile);
  };

  return (
    <div className="bg-white dark:bg-gray-800 rounded-lg shadow-lg p-6">
      <h2 className="text-2xl font-bold text-gray-900 dark:text-white mb-6 flex items-center gap-2">
        <Code className="h-6 w-6" />
        JWT Token Decoder
      </h2>

      <div className="space-y-4">
        {/* Token Input */}
        <div>
          <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
            JWT Token
          </label>
          <div className="relative">
            <textarea
              value={token}
              onChange={(e) => setToken(e.target.value)}
              placeholder="Paste your JWT token here..."
              className="w-full p-3 border border-gray-300 dark:border-gray-600 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 dark:bg-gray-700 dark:text-white resize-none"
              rows={showToken ? 6 : 3}
              style={{ fontFamily: showToken ? 'monospace' : 'inherit' }}
            />
            <button
              onClick={() => setShowToken(!showToken)}
              className="absolute top-3 right-3 text-gray-400 hover:text-gray-600"
            >
              {showToken ? <EyeOff size={20} /> : <Eye size={20} />}
            </button>
          </div>
        </div>

        {/* Buttons */}
        <div className="flex gap-3">
          <button
            onClick={handleDecode}
            className="px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors flex items-center gap-2"
          >
            <Code size={18} />
            Decode Token
          </button>
          
          <button
            onClick={loadExampleToken}
            className="px-4 py-2 bg-green-600 text-white rounded-lg hover:bg-green-700 transition-colors"
          >
            Use Your Example Token
          </button>
        </div>

        {/* Decoded Result */}
        {decodedProfile && (
          <div className="mt-6 p-4 bg-blue-50 dark:bg-blue-900/20 rounded-lg">
            <h3 className="text-lg font-semibold text-gray-900 dark:text-white mb-4 flex items-center gap-2">
              <User className="h-5 w-5" />
              Decoded Profile Information
            </h3>
            
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div>
                <span className="font-medium text-gray-700 dark:text-gray-300">Name:</span>
                <p className="text-gray-900 dark:text-white">{decodedProfile.name}</p>
              </div>
              
              <div>
                <span className="font-medium text-gray-700 dark:text-gray-300">Email:</span>
                <p className="text-gray-900 dark:text-white">{decodedProfile.email}</p>
              </div>
              
              <div>
                <span className="font-medium text-gray-700 dark:text-gray-300">User ID:</span>
                <p className="text-gray-900 dark:text-white font-mono text-sm">{decodedProfile.id}</p>
              </div>
              
              <div>
                <span className="font-medium text-gray-700 dark:text-gray-300">Token Issued:</span>
                <p className="text-gray-900 dark:text-white text-sm">
                  {new Date(decodedProfile.iat * 1000).toLocaleString()}
                </p>
              </div>
              
              <div className="md:col-span-2">
                <span className="font-medium text-gray-700 dark:text-gray-300">Token Expires:</span>
                <p className="text-gray-900 dark:text-white text-sm">
                  {new Date(decodedProfile.exp * 1000).toLocaleString()}
                  <span className="ml-2 text-gray-500">
                    ({Math.floor((decodedProfile.exp * 1000 - Date.now()) / (1000 * 60 * 60))} hours remaining)
                  </span>
                </p>
              </div>
            </div>

            {/* Raw JSON */}
            <details className="mt-4">
              <summary className="cursor-pointer text-sm font-medium text-gray-700 dark:text-gray-300">
                Show Raw JSON
              </summary>
              <pre className="mt-2 p-3 bg-gray-100 dark:bg-gray-800 rounded text-sm overflow-x-auto">
                {JSON.stringify(decodedProfile, null, 2)}
              </pre>
            </details>
          </div>
        )}

        {/* Usage Instructions */}
        <div className="mt-6 p-4 bg-yellow-50 dark:bg-yellow-900/20 rounded-lg">
          <h4 className="font-medium text-yellow-800 dark:text-yellow-200 mb-2">How to use this:</h4>
          <ul className="text-sm text-yellow-700 dark:text-yellow-300 space-y-1">
            <li>• Extract the token from your OAuth redirect URL</li>
            <li>• Paste it in the input above</li>
            <li>• Click "Decode Token" to see the profile information</li>
            <li>• The auth utilities automatically handle this in your app</li>
          </ul>
        </div>
      </div>
    </div>
  );
};

export default TokenDecoder;
